import type {
  AjusteDeReposicao,
  AlteracaoDeProfessor,
  AlteracaoDeTurma,
  AlunosDaAula,
  AulasDoDia,
  CandidatoAReposicao,
  Chamada,
  ChamadaDaAula,
  ContaDeProfessor,
  DefinicaoDeAlocacao,
  DetalheDaTurma,
  FrequenciaDasTurmas,
  ListaDeTurmas,
  ModalidadeAlocavel,
  MotivoDaTurma,
  NovaMatricula,
  NovaTurma,
  NovoProfessor,
  ProfessorNaLista,
  QuadroDeAlocacao,
  ReativacaoDeTurma,
  ResultadoDaAlocacao,
  ResultadoDaChamada,
  ResultadoDaTurma,
  ResultadoDeMatricula,
  ResultadoDeSaida,
  ResultadoDeTransferencia,
  RiscoDeCancelamento,
  SaidaDaEspera,
  SaidaDaTurma,
  SugestaoDeHorario,
  Transferencia,
  TurmasDoAluno,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas de turmas — `#v-turmas`, `#m-turma`, `#m-chamada`,
 * `#m-conflito`, a aba "Alunos da turma" de `#m-evento` e as abas Turmas e
 * Presenças de `#m-aluno`.
 *
 * A chamada é da AULA (a ocorrência na grade), não da turma: por isso ela
 * mora em `/aulas/:marcacaoId`, com o id do card da agenda.
 *
 * Os tipos são os de `@arena/shared/schemas`, os mesmos que o backend usa
 * para validar e responder.
 *
 * Ver: Arena_backend/Docs/plano/10-turmas-professores-chamada.md
 */
export const turmasApi = {
  listar(): Promise<ListaDeTurmas> {
    return api<ListaDeTurmas>('/turmas');
  },

  detalhe(id: string): Promise<DetalheDaTurma> {
    return api<DetalheDaTurma>(`/turmas/${id}`);
  },

  criar(dados: NovaTurma): Promise<ResultadoDaTurma> {
    return api<ResultadoDaTurma>('/turmas', { metodo: 'POST', corpo: dados });
  },

  alterar(id: string, dados: AlteracaoDeTurma): Promise<ResultadoDaTurma> {
    return api<ResultadoDaTurma>(`/turmas/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  suspender(id: string, dados: MotivoDaTurma): Promise<ResultadoDaTurma> {
    return api<ResultadoDaTurma>(`/turmas/${id}/suspender`, { metodo: 'POST', corpo: dados });
  },

  reativar(id: string, dados: ReativacaoDeTurma = {}): Promise<ResultadoDaTurma> {
    return api<ResultadoDaTurma>(`/turmas/${id}/reativar`, { metodo: 'POST', corpo: dados });
  },

  encerrar(id: string, dados: MotivoDaTurma): Promise<ResultadoDaTurma> {
    return api<ResultadoDaTurma>(`/turmas/${id}/encerrar`, { metodo: 'POST', corpo: dados });
  },

  frequencia(): Promise<FrequenciaDasTurmas> {
    return api<FrequenciaDasTurmas>('/turmas/frequencia');
  },

  risco(): Promise<RiscoDeCancelamento> {
    return api<RiscoDeCancelamento>('/turmas/risco');
  },

  /** Só modalidade ALOCÁVEL: a personalizada não reserva horário. */
  sugestao(modalidade: ModalidadeAlocavel): Promise<SugestaoDeHorario | null> {
    return api<SugestaoDeHorario | null>(`/turmas/sugestao?modalidade=${modalidade}`);
  },

  // -------------------------------------------------------------------
  // Matrícula e lista de espera
  // -------------------------------------------------------------------

  /** Com vaga, matricula; sem vaga, põe na fila — a resposta diz qual. */
  matricular(turmaId: string, dados: NovaMatricula): Promise<ResultadoDeMatricula> {
    return api<ResultadoDeMatricula>(`/turmas/${turmaId}/matriculas`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  sair(matriculaId: string, dados: SaidaDaTurma): Promise<ResultadoDeSaida> {
    return api<ResultadoDeSaida>(`/turmas/matriculas/${matriculaId}/sair`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  transferir(matriculaId: string, dados: Transferencia): Promise<ResultadoDeTransferencia> {
    return api<ResultadoDeTransferencia>(`/turmas/matriculas/${matriculaId}/transferir`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  sairDaEspera(entradaId: string, dados: SaidaDaEspera = {}): Promise<void> {
    return api<void>(`/turmas/lista-espera/${entradaId}/sair`, { metodo: 'POST', corpo: dados });
  },

  // -------------------------------------------------------------------
  // Aulas e chamada
  // -------------------------------------------------------------------

  /** "Aulas de hoje" — sem data, o servidor responde hoje no fuso da arena. */
  aulasDoDia(data?: string): Promise<AulasDoDia> {
    return api<AulasDoDia>(data ? `/aulas?data=${encodeURIComponent(data)}` : '/aulas');
  },

  chamada(marcacaoId: string): Promise<ChamadaDaAula> {
    return api<ChamadaDaAula>(`/aulas/${marcacaoId}/chamada`);
  },

  /** Registrar e refazer são a mesma operação: a chamada inteira. */
  registrarChamada(marcacaoId: string, dados: Chamada): Promise<ResultadoDaChamada> {
    return api<ResultadoDaChamada>(`/aulas/${marcacaoId}/chamada`, { metodo: 'PUT', corpo: dados });
  },

  alunosDaAula(marcacaoId: string): Promise<AlunosDaAula> {
    return api<AlunosDaAula>(`/aulas/${marcacaoId}/alunos`);
  },

  candidatosAReposicao(marcacaoId: string, busca: string): Promise<CandidatoAReposicao[]> {
    return api<CandidatoAReposicao[]>(
      `/aulas/${marcacaoId}/reposicao?busca=${encodeURIComponent(busca)}`,
    );
  },

  // -------------------------------------------------------------------
  // Ficha do aluno e reposições
  // -------------------------------------------------------------------

  doAluno(alunoId: string): Promise<TurmasDoAluno> {
    return api<TurmasDoAluno>(`/turmas/alunos/${alunoId}`);
  },

  ajustarReposicao(dados: AjusteDeReposicao): Promise<{ saldo: number }> {
    return api<{ saldo: number }>('/turmas/reposicoes/ajustes', { metodo: 'POST', corpo: dados });
  },

  // -------------------------------------------------------------------
  // Professores e alocação fixa
  // -------------------------------------------------------------------

  professores(): Promise<ProfessorNaLista[]> {
    return api<ProfessorNaLista[]>('/turmas/professores');
  },

  contasDeProfessor(): Promise<ContaDeProfessor[]> {
    return api<ContaDeProfessor[]>('/turmas/professores/contas');
  },

  criarProfessor(dados: NovoProfessor): Promise<ProfessorNaLista> {
    return api<ProfessorNaLista>('/turmas/professores', { metodo: 'POST', corpo: dados });
  },

  alterarProfessor(id: string, dados: AlteracaoDeProfessor): Promise<ProfessorNaLista> {
    return api<ProfessorNaLista>(`/turmas/professores/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  quadroDeAlocacao(): Promise<QuadroDeAlocacao> {
    return api<QuadroDeAlocacao>('/turmas/alocacoes');
  },

  definirAlocacao(dados: DefinicaoDeAlocacao): Promise<ResultadoDaAlocacao> {
    return api<ResultadoDaAlocacao>('/turmas/alocacoes', { metodo: 'PUT', corpo: dados });
  },
};
